import { Dayjs } from 'dayjs';
import { forwardRef, useImperativeHandle, useMemo } from 'react';
import { DatePicker, FocusRef } from '../../components';
import { createFocusHandle, createOnBlur } from './focusHandlers';
import { FilterInputProps } from './FilterInputProps';
import {
  DATE_TIME_FORMAT_EXTRA_SPACE,
  FormattedDateTime,
  getDayjs,
} from '../../utils';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './ValidTimePeriodFilterInput.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'time-period-filter-input'
);
addTranslations(translations);

export const ValidTimePeriodFilterInput = forwardRef<
  FocusRef,
  FilterInputProps
>(({ header, onBlur }, ref) => {
  const { t } = useTranslation();
  const id = `${header.column.id}-filter-input`;

  useImperativeHandle(ref, () => createFocusHandle(id));
  const handleOnBlur = useMemo(
    () => createOnBlur([id, `${id}-popup`], onBlur),
    [id, onBlur]
  );

  const filterValue = header.column.getFilterValue() as
    | [FormattedDateTime | null, FormattedDateTime | null]
    | undefined;

  const value = useMemo(
    () =>
      filterValue?.map((v) => {
        if (v) {
          return getDayjs(v, DATE_TIME_FORMAT_EXTRA_SPACE) ?? null;
        }
        return null;
      }) as [Dayjs | null, Dayjs | null],
    [filterValue]
  );

  return (
    <DatePicker.RangePicker
      id={id}
      className={id}
      classNames={{
        popup: { root: `${id}-popup` },
      }}
      allowEmpty={[true, true]}
      format={DATE_TIME_FORMAT_EXTRA_SPACE}
      allowClear
      placeholder={[t('placeholders.start'), t('placeholders.end')]}
      value={value}
      onChange={(_dayjsDates, formattedDates) =>
        header.column.setFilterValue(
          formattedDates?.some(Boolean) ? formattedDates : undefined
        )
      }
      // Prevent the event from bubbling up to a dropdown component
      onKeyDown={(event) => {
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.stopPropagation();
      }}
      onBlur={handleOnBlur}
      showTime
    />
  );
});
